import { List } from 'immutable';

/**
 * Fold nested modifiers into their parent value's display row.
 *
 * A nested modifier entry stores its parent value's id at
 * `modifier.parent_modifier_value_id`. This flattens an item's flat `modifiers`
 * list into display rows: one row per top-level chosen value, with any nested
 * modifier values (recursively, depth-first) folded into that row's `segments`
 * chain. Pure — no selectors; price summing and value formatting stay in the
 * consuming components.
 *
 * When no entry carries a `parent_modifier_value_id` (e.g. an order-history
 * payload that did not echo it back), nothing folds and every value becomes its
 * own single-segment row — identical to the previous flat rendering.
 *
 * @param {List} modifiers - an item's Immutable `modifiers` list, or null/undefined.
 * @returns {Array<{ modifierName: string, segments: Array<{value, type, quantity}>,
 *   values: Array<Immutable.Map>, hasPriceAdjustment: boolean }>}
 */
export const groupModifiersForDisplay = modifiers => {
  if (!modifiers || !modifiers.size) return [];

  // value id -> child modifier entries that name it as parent
  const childrenByParentValueId = {};
  modifiers.forEach(entry => {
    if (!entry) return;
    const parentId = entry.getIn(['modifier', 'parent_modifier_value_id']);
    if (parentId == null) return;
    (childrenByParentValueId[parentId] = childrenByParentValueId[parentId] || []).push(entry);
  });

  const rows = [];

  modifiers.forEach(entry => {
    if (!entry) return;
    if (entry.getIn(['modifier', 'parent_modifier_value_id']) != null) return; // nested — folded into its parent

    const modifierType = entry.getIn(['modifier', 'type']);
    (entry.get('values') || List()).forEach(value => {
      const segments = [];
      const values = [];
      const visited = new Set();

      const collect = (val, type) => {
        const id = val.get('id');
        if (id != null) {
          if (visited.has(id)) return; // cycle guard
          visited.add(id);
        }
        segments.push({ value: val.get('value'), type, quantity: val.get('quantity') });
        values.push(val);

        const children = id != null ? childrenByParentValueId[id] : undefined;
        if (children) {
          children.forEach(childEntry => {
            const childType = childEntry.getIn(['modifier', 'type']);
            (childEntry.get('values') || List()).forEach(childValue => collect(childValue, childType));
          });
        }
      };

      collect(value, modifierType);

      rows.push({
        modifierName: entry.getIn(['modifier', 'name']),
        segments,
        values,
        hasPriceAdjustment: values.some(v => v.get('price_adjustment')),
      });
    });
  });

  return rows;
};
